import React, { useState, useEffect } from 'react'
import classNames from 'classnames'
import { usePageContext } from '@toasttab/shared/src/hooks/usePageContext'
import { ParentMenus as DesktopParentMenus } from '@toasttab/shared/src/components/organisms/Navigation/Desktop/ParentMenus'
import { ParentMenus as MobileParentMenus } from '@toasttab/shared/src/components/organisms/Navigation/Mobile/ParentMenus'
import MainNavigationContext from '@toasttab/shared/src/data/HeaderNavigation/context/MainNavigationContext'
import Button from '@toasttab/shared/src/components/atoms/Button/Button'
import { determinePhoneAvailability } from '@toasttab/shared/src/components/organisms/Navigation/PhoneNumberLink'
import { MainNavigationProps } from '@toasttab/shared/src/data/HeaderNavigation/MainNavigationMenu'
import { useI18next } from '@toasttab/shared/src/hooks/useI18next'
import _helpers from '@toasttab/shared/src/globals/_helpers'

const MainNavigation = ({ navigationData }: MainNavigationProps) => {
  const { t } = useI18next()
  const [selectedParentMenu, setSelectedParentMenu] = useState(null)
  const [mobileNavOpen, setMobileNavOpen] = useState(null)
  const [isAvailable, setIsAvailable] = useState(false)

  const pageContext = usePageContext()
  const { siteRegion } = navigationData

  const headerStyle = pageContext?.headerStyle || 'Default'
  const showMainNavContent = headerStyle === 'Default'
  const showLogin = ['Default', 'Logo & Login'].includes(headerStyle)
  const showSales = siteRegion === 'us'

  const navHeapClass =
    siteRegion === 'us' ? `topnav_entire_v` : `${siteRegion}_topnav_entire`
  const rxHeapClass = `${siteRegion}_topnav_restaurants`
  const demoHeapClass = `${siteRegion}_topnav_demo`
  const loginHeapClass = `${siteRegion}_topnav_login`

  const navCss = classNames(
    'flex flex-nowrap z-[100] bg-white content-center items-end justify-between w-full h-[56px] min-[992px]:h-[72px] font-sansPro pt-6',
    {
      'border-b border-b-transparent min-[992px]:border-gray-50':
        headerStyle !== 'Logo Only'
    }
  )
  const baseHamburgerCss =
    'block absolute h-0.5 w-5 bg-gray-125 transform transition duration-500 ease-in-out'
  const hamburgerTopCss = classNames(baseHamburgerCss, {
    'rotate-45': mobileNavOpen,
    '-translate-y-1.5': !mobileNavOpen
  })
  const hamburgerMidCss = classNames(baseHamburgerCss, {
    'opacity-0': mobileNavOpen
  })
  const hamburgerBottomCss = classNames(baseHamburgerCss, {
    '-rotate-45': mobileNavOpen,
    'translate-y-1.5': !mobileNavOpen
  })

  const loginButtonCss =
    'leading-none rounded-md border-2 border-blue-75 hover:border-blue-100'
  const loginCss = classNames(
    'font-semibold text-blue-75 mr-6 font-sansPro',
    loginHeapClass,
    {
      [loginButtonCss]: pageContext?.styleContext === 'request-demo',
      'py-3 px-6': pageContext?.styleContext === 'request-demo'
    }
  )
  const mobileLoginCss = classNames('text-blue-75 font-semibold mr-4 mb-1', {
    [loginButtonCss]: pageContext?.styleContext === 'request-demo',
    'p-3': pageContext?.styleContext === 'request-demo'
  })

  const salesCtaCss = classNames(
    'px-2 py-1 text-[0.75rem] font-normal leading-[0.875rem] rounded-[999px] mr-1',
    {
      'bg-grass-25': isAvailable,
      'bg-gray-50': !isAvailable
    }
  )

  // Request Demo Sales CTA styling
  const demoSalesCss = classNames(
    'flex items-center mr-4 after:content-[""] after:w-[10px] after:h-[10px] after:rounded-full after:ml-2',
    {
      'after:bg-grass-25': isAvailable,
      'after:bg-gray-25': !isAvailable
    }
  )
  const mobileDemoSalesCss = classNames(
    'flex items-center mr-3 font-normal whitespace-nowrap before:content-[""] before:w-[10px] before:h-[10px] before:rounded-full before:mr-2',
    {
      'before:bg-grass-25': isAvailable,
      'before:bg-gray-25': !isAvailable
    }
  )

  const toggleMobileMenu = () => {
    setMobileNavOpen(!mobileNavOpen)
    setSelectedParentMenu(null)
  }

  useEffect(() => {
    setIsAvailable(
      determinePhoneAvailability(
        pageContext.globalSettings?.mobileSalesIndicatorSchedule?.schedule
      )
    )

    const handleMobileEsc = (event: any) => {
      if (
        event.key === 'Escape' ||
        event.key === 'ESC' ||
        event.keyCode === 27
      ) {
        setSelectedParentMenu(null)
        setMobileNavOpen(false)
      }
    }

    window.addEventListener('keydown', handleMobileEsc)
  }, [])

  return (
    <MainNavigationContext.Provider
      value={{
        selectedParentMenu,
        mobileNavOpen,
        setSelectedParentMenu,
        setMobileNavOpen
      }}
    >
      <nav
        aria-label='Toast Main Navigation'
        className={`${navHeapClass} relative z-[100]`}
      >
        <div className={navCss}>
          <DesktopParentMenus
            navigationData={navigationData}
            showDropdowns={showMainNavContent}
          />
          <div className='hidden min-[992px]:flex flex-nowrap items-center pb-[12px] mr-4 md:mr-6 min-[992px]:mr-7 min-[1440px]:mr-[156px]'>
            {showMainNavContent && siteRegion === 'us' ? (
              <>
                <a
                  className={`${rxHeapClass} font-sansPro text-base font-semibold text-blue-75 before:h-6 before:w-6 before:content-icon-blue-map-marker before:mr-2 hidden min-[1440px]:block`}
                  href={navigationData.restaurantUrl}
                >
                  {t('Restaurants')}
                </a>
                <span className='mx-3 font-semibold text-blue-75 hidden min-[1440px]:block'>
                  |
                </span>
              </>
            ) : null}
            {pageContext?.styleContext === 'request-demo' && (
              <a className={demoSalesCss} href='tel:8574107412'>
                {t('Contact Sales')}: (857) 410-7412
              </a>
            )}
            {showLogin ? (
              <a className={loginCss} href={navigationData.loginUrl}>
                {t('Login')}
              </a>
            ) : null}
            {showMainNavContent ? (
              <Button
                classes={`${demoHeapClass} !font-semibold`}
                href={navigationData.demoUrl}
              >
                {t('Get a Demo')}
              </Button>
            ) : null}
          </div>
          {showMainNavContent ? (
            <div className='flex min-[992px]:hidden flex-nowrap items-center pb-[18px] mr-4 md:mr-6 min-[992px]:mr-7 font-sansPro'>
              {showSales ? (
                <>
                  <div className={salesCtaCss}>{t('Call Sales')}</div>
                  <a
                    className='text-[0.875rem] leading-[1rem] text-blue-75 mr-4 md:mr-14'
                    href='tel:6172971005'
                  >
                    (617) 297-1005
                  </a>
                </>
              ) : null}
              <button
                aria-controls='mobile-nav-menu'
                aria-expanded={mobileNavOpen ? true : false}
                aria-haspopup='true'
                aria-label='Toggle Toast Mobile Navigation'
                className='w-5 h-5'
                onClick={toggleMobileMenu}
              >
                <span aria-hidden='true' className={hamburgerTopCss} />
                <span aria-hidden='true' className={hamburgerMidCss} />
                <span aria-hidden='true' className={hamburgerBottomCss} />
              </button>
            </div>
          ) : null}
          {showLogin && !showMainNavContent ? (
            <div className='flex items-center min-[992px]:hidden'>
              {pageContext?.styleContext === 'request-demo' && (
                <div className='relative'>
                  <a className={mobileDemoSalesCss} href='tel:8574107412'>
                    {t('Sales')}: (857) 410-7412
                  </a>
                  {isAvailable ? (
                    <div className='absolute top-[calc(100%+4px)] left-1/2 transform -translate-x-1/2 bg-grass-25 px-2 py-1 text-xs font-normal rounded-xl text-gray-125 uppercase font-semibold tracking-[0.1em] leading-none whitespace-nowrap before:content-[""] before:bg-grass-25 before:w-[8px] before:h-[8px] before:absolute before:top-[-4px] before:z-[-1] before:left-1/2 before:transform before:-translate-x-1/2 before:rotate-45'>
                      {t('Now Available')}
                    </div>
                  ) : null}
                </div>
              )}
              <a className={mobileLoginCss} href={navigationData.loginUrl}>
                {t('Login')}
              </a>
            </div>
          ) : null}
        </div>
        {showMainNavContent ? (
          <MobileParentMenus navigationData={navigationData} />
        ) : null}
      </nav>
    </MainNavigationContext.Provider>
  )
}

export default MainNavigation
